import createSagaMiddleware from 'redux-saga'
import { AnyAction, Middleware, Store, configureStore } from '@reduxjs/toolkit'
import { useSelector as _useSelector } from 'react-redux'
import { all, call } from 'redux-saga/effects'

import * as api from '../api'
import * as env from '../env'
import * as helpers from '../helpers'
import * as schema from '../schema'
import * as utils from '../utils'
import {
  GetUserEmail,
  GetUserID,
  Storage,
  setStorage,
  setUserEmailGetter,
  setUserIDGetter,
} from '../api'

// import * as costs from './costs'
import { bookmarks, bookmarksSaga } from './bookmarks'
import { setDispatcher, updatedCustomerActionCreator } from './common'
import { customers, customersSaga } from './customers'
import { closers, closersSaga } from './closers'
import { efficiencyPrices, efficiencyPriceSaga } from './efficiencyPrices'
import { env as envReducer, envSaga } from './env'
import { invoicesSaga, invoices } from './invoices'
import { itemPrices, itemPricesSaga } from './itemPrices'
import { media, mediaSaga } from './media'
import { session } from './session'
import { setters, settersSaga } from './setters'
import { thumbnails } from './thumbnails'

const sagaMiddleware = createSagaMiddleware({
  onError(e) {
    utils.log('saga error', e)
  },
})

let actionsTypeLoggerEnabled = false
let actionsPayloadLoggerEnabled = false
const loggerMiddleware: Middleware = (_) => (next) => (action) => {
  const a = action as { type?: string; payload?: unknown }
  if (actionsTypeLoggerEnabled || actionsPayloadLoggerEnabled) {
    utils.log('----Store logger---')
  }

  if (actionsTypeLoggerEnabled) {
    utils.log(a.type)
  }

  if (actionsPayloadLoggerEnabled) {
    utils.log('---Payload---')
    utils.log(a.payload)
  }

  if (actionsTypeLoggerEnabled || actionsPayloadLoggerEnabled) {
    utils.log('////Store logger---')
  }

  next(action)
}

const reducers = {
  bookmarks,
  closers,
  // costs: costs.costs,
  customers,
  efficiencyPrices,
  env: envReducer,
  invoices,
  itemPrices,
  media,
  session,
  setters,
  thumbnails,
} as const

export type State = utils.DeepReadonly<{
  [K in keyof typeof reducers]: ReturnType<(typeof reducers)[K]>
}>

let exposedStore: Store<State> | undefined

export const dispatch = (action: unknown) => {
  if (!exposedStore) {
    throw new Error('Tried to dispatch() before initializing the store.')
  }
  exposedStore.dispatch(action as AnyAction)
}

export const getStore = () => exposedStore as Store<State>

export interface InitStoreParams {
  fireStorage?: Storage
  getUserEmail?: GetUserEmail
  getUserID?: GetUserID
}

export const initStore = ({
  fireStorage,
  getUserEmail,
  getUserID,
}: InitStoreParams = {}) => {
  if (!fireStorage) {
    throw new ReferenceError(`fireStorage must be provided to initStore()`)
  }
  setStorage(fireStorage)

  if (!getUserEmail) {
    throw new ReferenceError(`getUserEmail must be provided to initStore()`)
  }
  setUserEmailGetter(getUserEmail)

  if (!getUserID) {
    throw new ReferenceError(`getUserID must be provided to initStore()`)
  }
  setUserIDGetter(getUserID)

  const store = configureStore({
    reducer: reducers,
    middleware(getDefaultMiddleware) {
      const defaultMiddleware = getDefaultMiddleware({
        serializableCheck: { ignoredPaths: ['settings'] },
        thunk: false,
      })

      return defaultMiddleware
        .prepend(loggerMiddleware as Middleware)
        .concat(sagaMiddleware as Middleware)
    },
  })

  setDispatcher(store.dispatch.bind(store))

  exposedStore = store

  function* rootSaga() {
    yield all([
      call(bookmarksSaga),
      call(closersSaga),
      call(customersSaga),
      call(efficiencyPriceSaga),
      call(envSaga),
      call(invoicesSaga),
      call(itemPricesSaga),
      call(mediaSaga),
      call(settersSaga),
    ])
  }

  sagaMiddleware.run(rootSaga)

  return store
}

export const useSelector = <TSelected>(
  selector: (state: State) => TSelected,
): TSelected => _useSelector<State, TSelected>(selector)

export const updateCustomer = (
  customerID: string,
  _data: schema.PartialCustomer,
  opts?: { skipModifyDate?: boolean | undefined | null },
) => {
  const data = { ..._data }
  const currState = getStore().getState()
  const currCustomer =
    currState.customers.data[customerID] ||
    schema.createEmptyCustomer({
      firebaseKey: customerID,
    })
  const efficiencyPrices = currState.efficiencyPrices.prices

  if (data.air_conditioner_unit_type?.startsWith('roof_')) {
    data.air_conditioner_furnance = 'yes'
  }
  if (
    data.air_conditioner_unit_type?.startsWith('split_') &&
    currCustomer.air_conditioner_working === 'yes' &&
    currCustomer.air_conditioner_furnance === 'no'
  ) {
    data.air_conditioner_new_tons = data.air_conditioner_new_tons =
      currCustomer.air_conditioner_current_tons
  }
  if (data.air_conditioner_furnance === 'no') {
    data.air_conditioner_new_tons = currCustomer.air_conditioner_current_tons
  }
  if (data.air_conditioner_working === 'no') {
    data.air_conditioner_replace_ducts = 'no'
    data.air_conditioner_furnance = 'yes'
  }
  if (
    data.air_conditioner_current_tons &&
    currCustomer.air_conditioner_working === 'yes' &&
    currCustomer.air_conditioner_unit_type.startsWith('split_') &&
    currCustomer.air_conditioner_furnance === 'no'
  ) {
    data.air_conditioner_new_tons = data.air_conditioner_current_tons
  }

  if (typeof data.main_panel_upgrade_installation_company !== 'undefined') {
    console.debug(
      'updateCustomer() -> installer change, deleting battery type and upgrade needed/requested',
    )
    data.battery_type = ''
    data.main_panel_upgrade_needed_or_requested = ''
  }
  if (typeof data.battery_type !== 'undefined') {
    console.debug(
      'updateCustomer() -> battery type change, deleting battery size',
    )
    data.battery_size = ''
  }
  if (typeof data.roof_layover_or_tear !== 'undefined') {
    console.debug(
      'updateCustomer() -> roof type change, deleting layers amount',
    )
    data.roof_layers_how_many = ''
  }
  if (data.roof_layover_or_tear === schema.RoofWorkType.ReRoofWoodShake) {
    data.roof_tear_material = '30'
  }

  if (env.CODENAME === 'nirvana') {
    // Should be ideally handled in a saga in the future
    ;(async () => {
      const twoStoryFeeSS = await api
        .getDatabase()
        .ref('Misc/env/twoStoryFee')
        .once('value')
      const twoStoryFee = twoStoryFeeSS.val() as number | null | undefined

      const sharedContractUpdate: Partial<helpers.SharedContractNirvana> = {
        rows: helpers.buildContractRows({
          ...currCustomer,
          ...data,
        }),
        totalCostCustomer: helpers.calcTotalCost({
          customer: {
            ...currCustomer,
            ...data,
          },
          twoStoryFee: twoStoryFee || 0,
          type: 'customer',
        }),
      }
      api
        .getDatabase()
        .ref(`Contract/${customerID}`)
        .update(sharedContractUpdate)
    })()

    if (data.homeStoryNumber === '2' || data.homeStoryNumber === '1') {
      data.globalNotes = helpers.withTwoStoryFeeNote(
        currCustomer.globalNotes || '',
        data.homeStoryNumber === '2',
      )
    }
  }

  for (const [k, v] of utils.entries(data)) {
    const dependents = helpers.yesNoToDependent[k]

    if (dependents && v === 'no') {
      const [dependent] = dependents

      const sanitizer =
        schema.fieldTypeToSanitizer[
          schema.CustomerSchema[dependent] as unknown as string
        ]

      // @ts-expect-error
      data[dependent] = sanitizer(null)
    }
  }

  /**
   * Presumably the lock is working fine and it has been unlocked and it's now
   * being edited as normal.
   */
  const costsFieldsChanged = utils
    .keys(data)
    .filter(
      (field) =>
        field.endsWith('_cost') &&
        !field.endsWith('_customer_cost') &&
        !field.includes('touched'),
    )

  /**
   * For any costs manually inputted, replicate to customer cost if it's the
   * linked state.
   */
  for (const field of costsFieldsChanged) {
    const efficiency = field.replace('_cost', '') as schema.CustomerField
    if (!currCustomer.has_touched_customer_cost[efficiency]) {
      const customerCostField = (efficiency +
        '_customer_cost') as schema.CustomerField
      // @ts-ignore ??
      data[customerCostField] = data[field]
    }
  }

  const efficienciesChanged = utils
    .entries(data)
    .map(([field, val]) => {
      if (field.endsWith('_cost')) {
        return null
      }
      if (helpers.inputToEfficiency[field]) {
        return helpers.inputToEfficiency[field]
      }
      if (
        field in helpers.efficiencyToInputs
        // Used to filter for 'yes', now allow for global calculator to act
        // && data[field] === 'yes'
      ) {
        return field
      }
      if (field === 'efficiency_updated') return val as schema.CustomerField

      return null
    })
    .filter((_) => !!_) as schema.CustomerField[]

  if (env.IS_RN) {
    // Account for always on efficiencies in Security section on mobile
    for (const efficiency of efficienciesChanged.filter((efficiency) =>
      efficiency.startsWith('security_'),
    )) {
      if (data[efficiency] === 'no') {
        // Do not re-activate
        break
      }
      // B117: a section the user already turned to "No" must stay off when
      // only one of its sub-fields (detail list, qty, notes) is edited -
      // this update call didn't touch the yes/no field itself, so leave it
      // alone instead of silently flipping it back to "Yes".
      if (data[efficiency] === undefined && currCustomer[efficiency] === 'no') {
        continue
      }
      // Activate if any data was inputted, so it shows on the report/etc
      //@ts-ignore
      data[efficiency] = 'yes'
    }
  }

  if (data.main_panel_upgrade_installation_company) {
    if (currCustomer.main_panel_upgrade === 'yes') {
      console.debug(
        "updateCustomer() -> installer changed while MPU activated, re-calc'ing cost for MPU",
      )
      efficienciesChanged.push('main_panel_upgrade')
    }
    if (currCustomer.derate === 'yes') {
      console.debug(
        "updateCustomer() -> installer changed while Derate activated, re-calc'ing cost for Derate",
      )
      efficienciesChanged.push('derate')
    }
    if (currCustomer.small_system === 'yes') {
      console.debug(
        "updateCustomer() -> installer changed while Small System activated, re-calc'ing cost for Small System",
      )
      efficienciesChanged.push('small_system')
    }
  }

  const globalCosts = helpers.globalCalculator(
    {
      ...currCustomer,
      ...data,
    },
    efficiencyPrices,
    env.getCompanyState(currCustomer.solarCompany) as
      | 'arizona'
      | 'california'
      | 'nevada',
    efficienciesChanged,
  )

  for (const efficiency of [
    ...efficienciesChanged,
    ...utils.keys(globalCosts),
  ]) {
    const calculator = helpers.efficiencyToCalculator[efficiency]

    if (typeof calculator !== 'function') {
      continue
    }

    try {
      const [unRoundedUnitCost, unitNotes] =
        globalCosts[efficiency] ||
        calculator(
          {
            ...currCustomer,
            ...data,
          },
          efficiencyPrices,
          env.getCompanyState(currCustomer.solarCompany) as
            | 'arizona'
            | 'california'
            | 'nevada',
        )

      // Priced per unit, times the Qty (13312379361)
      const units = helpers.efficiencyUnits(efficiency, {
        ...currCustomer,
        ...data,
      })
      const notes = helpers.notesWithUnits(unitNotes, units)
      const cost = Math.round(unRoundedUnitCost * units)
      const costStr = Number.isFinite(cost) && cost !== 0 ? cost.toString() : ''

      if (
        !currCustomer.has_touched_cost[efficiency] &&
        !currCustomer.locked_price[efficiency]
      ) {
        // @ts-ignore
        data[(efficiency + '_cost') as schema.CustomerField] = costStr

        if (!currCustomer.has_touched_customer_cost[efficiency]) {
          // @ts-ignore
          data[(efficiency + '_customer_cost') as schema.CustomerField] =
            costStr
        }
      }

      // The notes always follow the rows, even under a locked price: the lock
      // keeps the PRICE, and a frozen list made the Job Summary contradict the
      // Scope of Work (Charles Foster, Oct 19: a second camera and a Smart
      // Panel added under a locked WiFi Camera price never reached the Job
      // Summary).
      // @ts-ignore
      data[(efficiency + '_notes') as schema.CustomerField] = notes
    } catch (e) {
      utils.log(
        `Could not auto-gen cost/notes for efficiency ${efficiency} at customer ${customerID}`,
      )
      utils.log(e)

      if (
        !currCustomer.has_touched_cost[efficiency] &&
        !currCustomer.locked_price[efficiency]
      ) {
        // @ts-ignore
        data[(efficiency + '_cost') as schema.CustomerField] = ''
      }
      if (
        !currCustomer.has_touched_customer_cost[efficiency] &&
        !currCustomer.locked_price[efficiency]
      ) {
        // @ts-ignore
        data[(efficiency + '_customer_cost') as schema.CustomerField] = ''
      }

      if (!currCustomer.locked_price[efficiency]) {
        // @ts-ignore
        data[(efficiency + '_notes') as schema.CustomerField] = ''
      }
    }
  }

  if (data.has_touched_cost) {
    data.has_touched_cost = {
      ...currCustomer.has_touched_cost,
      ...data.has_touched_cost,
    }
  }
  if (data.has_touched_customer_cost) {
    const relinked = utils.pickBy(
      // @ts-ignore
      data.has_touched_customer_cost,
      (_) => !_,
    ) as unknown as Readonly<Record<string, boolean>>

    for (const relinkedCustomerCostEff of utils.keys(relinked)) {
      /**
       * The user just re-linked customer cost to the normal cost, therefore,
       * copy over the current normal cost.
       */
      // @ts-ignore
      data[
        (relinkedCustomerCostEff + '_customer_cost') as schema.CustomerField
      ] =
        currCustomer[
          (relinkedCustomerCostEff + '_cost') as schema.CustomerField
        ]
    }

    data.has_touched_customer_cost = {
      ...currCustomer.has_touched_customer_cost,
      ...data.has_touched_customer_cost,
    }
  }

  dispatch(updatedCustomerActionCreator({ customerID, data }))
  api._updateCustomer(customerID, data, opts)
}

// Per Jaron (Monday 12531259133): simply opening a customer should move it to
// the top of the list too, not just editing one. `sort_key` (V6) already
// drives that order, and updateCustomer() already bumps it to `-now` on any
// write — so an empty write does exactly that with no other field touched.
export const touchCustomerSortKey = (customerID: string): void => {
  updateCustomer(customerID, {})
}

export const resetEfficiency = (
  customerID: string,
  efficiency: schema.CustomerField,
) => {
  const currCustomer =
    getStore().getState().customers.data[customerID] ||
    schema.createEmptyCustomer({
      firebaseKey: customerID,
    })
  let batch: Partial<utils.Writable<schema.Customer>> = {}

  const relatedFields = schema.customerFields
    .filter(
      (k) =>
        k.startsWith(`${efficiency}_`) &&
        !schema.roofClaimingFields.includes(k as any),
    )
    .filter((k) => k !== 'main_panel_upgrade_installation_company')

  for (const relatedField of relatedFields) {
    batch = {
      ...batch,
      [relatedField]: (() => {
        if (schema.CustomerSchema[relatedField] === schema.YesNo) {
          return 'no'
        }
        if (schema.CustomerSchema[relatedField] === schema.NoYes) {
          return 'yes'
        }
        if (schema.CustomerSchema[relatedField] === schema.Bit) {
          return 0
        }
        if (schema.CustomerSchema[relatedField] === schema.number) {
          return 0
        }
        return ''
      })(),
    }
  }

  if (efficiency === 'new_windows')
    batch.new_windows_sqft_each_window = JSON.stringify([
      schema.newWindowGroup(),
    ])

  if (efficiency === 'mini_split')
    batch.mini_split_tons = JSON.stringify([schema.newMiniSplit()])

  api._updateCustomer(customerID, {
    [efficiency]: 'no',
    has_touched_cost: { ...currCustomer.has_touched_cost, [efficiency]: false },
    has_touched_customer_cost: {
      ...currCustomer.has_touched_customer_cost,
      [efficiency]: false,
    },
    locked_price: { ...currCustomer.locked_price, [efficiency]: false },
    ...batch,
  })
}

export const toggleLockPrice = (
  customerID: string,
  efficiency: schema.CustomerField,
): void => {
  const currCustomer =
    getStore().getState().customers.data[customerID] ||
    schema.createEmptyCustomer({ firebaseKey: customerID })

  api._updateCustomer(customerID, {
    locked_price: {
      ...currCustomer.locked_price,
      [efficiency]: !currCustomer.locked_price[efficiency],
    },
  })
}
